import { useCallback, useState } from "react";

import { AbfLoanExpanded, LoanInfoSorting, LoanPaginationInfo, LoanStatusType } from "@bridgesplit/abf-react";
import { isAll, SortDirection } from "@bridgesplit/utils";

import { useChunkedPaginationController } from "./shared";

export const DEFAULT_LOAN_PAGE_SIZE = 5;
export const DEFAULT_LOAN_PAGE_SIZE_OPTIONS = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 15, label: "15" },
    { value: 25, label: "25" }
];
const DEFAULT_SORT_SIDE = SortDirection.Desc;
const DEFAULT_SORT_TYPE = LoanInfoSorting.Principal;

export function useLoansPagination({
    initialPageSize = DEFAULT_LOAN_PAGE_SIZE,
    initialState
}: {
    initialPageSize?: number;
    initialState?: { principalMint?: string; collateralMint?: string };
}) {
    const [sortSide, setSortSide] = useState<SortDirection>(DEFAULT_SORT_SIDE);
    const [sortType, setSortType] = useState<LoanInfoSorting>(DEFAULT_SORT_TYPE);
    const [loanStatusType, setLoanStatusType] = useState<LoanStatusType>(LoanStatusType.ActiveOnly);
    const [principalMintState, setPrincipalMint] = useState<string | undefined>(undefined);
    const [collateralMintState, setCollateralMint] = useState<string | undefined>(undefined);
    const principalMint = principalMintState ?? initialState?.principalMint;
    const collateralMint = collateralMintState ?? initialState?.collateralMint;

    const chunkedPagination = useChunkedPaginationController<AbfLoanExpanded, LoanPaginationInfo>(
        {
            sortSide,
            sortType,
            loanStatusType,
            principalMint: isAll(principalMint) ? undefined : principalMint,
            collateralMint: isAll(collateralMint) ? undefined : collateralMint
        },
        initialPageSize
    );

    const reset = useCallback(() => {
        setSortSide(DEFAULT_SORT_SIDE);
        setSortType(DEFAULT_SORT_TYPE);

        setLoanStatusType(LoanStatusType.ActiveOnly);
        setPrincipalMint(undefined);
        setCollateralMint(undefined);
        chunkedPagination.resetPagination();
    }, [setSortSide, setSortType, setLoanStatusType, setPrincipalMint, setCollateralMint, chunkedPagination]);

    return {
        ...chunkedPagination,
        params: chunkedPagination.clientPagination,
        setSortSide,
        setSortType,
        setLoanStatusType,
        setPrincipalMint,
        setCollateralMint,
        reset
    };
}
